import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, initializeAuth } from 'firebase/auth';
// getReactNativePersistence is available at runtime but may not be in all type defs
const { getReactNativePersistence } = require('firebase/auth') as { getReactNativePersistence: any };
import { getFirestore, initializeFirestore, Firestore } from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { currentFirestoreTransport } from '../utils/firestoreTransport';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
};

// Debug: Log actual config values (first 10 chars)
console.log('🔍 Firebase config values:', {
  apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'MISSING',
  authDomain: firebaseConfig.authDomain || 'MISSING',
  projectId: firebaseConfig.projectId || 'MISSING',
  appId: firebaseConfig.appId ? `${firebaseConfig.appId.substring(0, 10)}...` : 'MISSING',
});

// Validate required config
const validateConfig = () => {
  // Validate the actual config object, not process.env (which is not available at runtime in browser)
  const requiredFields: (keyof typeof firebaseConfig)[] = ['apiKey', 'projectId', 'appId'];

  const missing = requiredFields.filter(key => !firebaseConfig[key]);

  if (missing.length > 0) {
    console.warn(
      `Firebase config missing required fields: ${missing.join(', ')}. ` +
      'Running in offline mode. See .env.example for setup instructions.'
    );
    return false;
  }

  return true;
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;

// Initialize Firebase only if config is valid
const isConfigValid = validateConfig();

if (isConfigValid) {
  try {
    // Initialize app if not already initialized
    if (getApps().length === 0) {
      app = initializeApp(firebaseConfig);
      console.log('✅ Firebase initialized successfully');
    } else {
      app = getApps()[0];
      console.log('✅ Firebase already initialized');
    }

    // Initialize Auth with AsyncStorage persistence for React Native
    // On web, Firebase handles persistence automatically
    if (Platform.OS !== 'web') {
      auth = initializeAuth(app, {
        persistence: getReactNativePersistence(AsyncStorage),
      });
    } else {
      auth = getAuth(app);
    }

    // Initialize Firestore. Long polling on WebKit, where a streamed answer can
    // sit unread for 30 s (#791); the SDK's own choice everywhere else.
    if (currentFirestoreTransport(Platform.OS === 'web') === 'long-polling') {
      try {
        firestore = initializeFirestore(app, { experimentalForceLongPolling: true });
      } catch {
        // Already initialized (a hot reload re-runs this module): keep that instance.
        firestore = getFirestore(app);
      }
    } else {
      firestore = getFirestore(app);
    }

    console.log('✅ Firebase services initialized');
  } catch (error) {
    console.error('❌ Firebase initialization error:', error);
  }
} else {
  console.warn('⚠️ Firebase not configured. App running in offline mode.');
}

// Export Firebase instances
export { app, auth, firestore };

// Export helper to check if Firebase is enabled
export const isFirebaseEnabled = () => isConfigValid && app !== null;

// Expose auth to window for Chrome extension access (web only)
if (Platform.OS === 'web' && typeof window !== 'undefined' && auth) {
  (window as any).firebaseAuth = auth;
  console.log('[Firebase] Auth instance exposed to window.firebaseAuth for extension access');

}
